import { z } from 'zod';
/* ------------------------------------------------------------------ */
/* Hằng số                                                             */
/* ------------------------------------------------------------------ */
export const SCHEDULED_POST_STATUSES = [
    'draft',
    'ready',
    'uploading',
    'scheduled_on_youtube',
    'published',
    'failed',
];
export const scheduledPostStatusSchema = z.enum(SCHEDULED_POST_STATUSES);
export const SCHEDULED_POST_STATUS_LABEL = {
    draft: 'Nháp',
    // `ready` = đủ thumbnail, nhạc và metadata, nhưng thẻ sản xuất **vẫn ở bước
    // Kiểm duyệt** (bất biến ở docs/modules/schedule.md). Gọi là "Sẵn sàng đăng"
    // khiến người đọc tưởng đã lên lịch trên YouTube.
    ready: 'Chờ kiểm duyệt',
    uploading: 'Đang tải lên',
    scheduled_on_youtube: 'Đã lên lịch trên YouTube',
    published: 'Đã đăng',
    failed: 'Lỗi',
};
/** Nhãn ngắn cho thẻ trên lịch và dòng tách số của trang Tổng quan. */
export const SCHEDULED_POST_STATUS_SHORT = {
    draft: 'nháp',
    ready: 'chờ kiểm duyệt',
    uploading: 'đang tải lên',
    scheduled_on_youtube: 'đã lên lịch',
    published: 'đã đăng',
    failed: 'lỗi',
};
/** Màu thẻ trên lịch, theo chú thích màu của thiết kế. */
export const SCHEDULED_POST_TONE = {
    draft: 'neutral',
    ready: 'info',
    uploading: 'info',
    scheduled_on_youtube: 'info',
    published: 'ok',
    failed: 'bad',
};
/** Thứ còn thiếu để một bài đăng chuyển sang `ready`. */
export const MISSING_ITEMS = ['thumbnail', 'music_approval', 'metadata'];
export const missingItemSchema = z.enum(MISSING_ITEMS);
export const MISSING_ITEM_LABEL = {
    thumbnail: 'Thiếu thumbnail',
    music_approval: 'Nhạc chưa được duyệt',
    metadata: 'Thiếu metadata',
};
export const CALENDAR_VIEWS = ['week', 'month'];
export const calendarViewSchema = z.enum(CALENDAR_VIEWS);
/* ------------------------------------------------------------------ */
/* Bài đăng                                                            */
/* ------------------------------------------------------------------ */
export const scheduledPostSchema = z.object({
    id: z.string(),
    productionItemId: z.string(),
    channelId: z.string(),
    channelName: z.string(),
    channelColor: z.string(),
    title: z.string(),
    /// Giờ công khai, lưu UTC. Giao diện hiển thị theo Asia/Ho_Chi_Minh.
    publishAt: z.iso.datetime(),
    market: z.string(),
    status: scheduledPostStatusSchema,
    missing: z.array(missingItemSchema),
    youtubeVideoId: z.string().nullable(),
    lastError: z.string().nullable(),
});
export const calendarQuerySchema = z.object({
    view: calendarViewSchema.default('week'),
    /// Ngày bất kỳ trong tuần hoặc tháng cần xem (YYYY-MM-DD).
    anchor: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    channelId: z.string().optional(),
});
export const calendarResponseSchema = z.object({
    view: calendarViewSchema,
    from: z.string(),
    to: z.string(),
    days: z.array(z.object({
        date: z.string(),
        posts: z.array(scheduledPostSchema),
    })),
    /// Cảnh báo tuần tới chưa đủ số video mục tiêu.
    warnings: z.array(z.object({
        channelId: z.string(),
        channelName: z.string(),
        ready: z.number().int().nonnegative(),
        target: z.number().int().nonnegative(),
    })),
});
/* ------------------------------------------------------------------ */
/* Input                                                               */
/* ------------------------------------------------------------------ */
export const createScheduledPostInputSchema = z.object({
    productionItemId: z.string().min(1),
    publishAt: z.iso.datetime(),
    market: z.string().trim().min(2).max(8).optional(),
});
export const updateScheduledPostInputSchema = z.object({
    publishAt: z.iso.datetime().optional(),
    status: scheduledPostStatusSchema.optional(),
});
export const postingTargetSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    videosPerWeek: z.number().int().nonnegative(),
    preferredSlots: z
        .array(z.object({
        weekday: z.number().int().min(1).max(7),
        hour: z.number().int().min(0).max(23),
        minute: z.number().int().min(0).max(59).default(0),
    }))
        .nullable(),
});
export const postingTargetInputSchema = z.object({
    videosPerWeek: z.number().int().min(0).max(50),
    preferredSlots: z
        .array(z.object({
        weekday: z.number().int().min(1).max(7),
        hour: z.number().int().min(0).max(23),
        minute: z.number().int().min(0).max(59).default(0),
    }))
        .nullish(),
});
/** Một dòng của `listUpcoming(range)`, dùng cho module dashboard. */
export const upcomingPostSchema = scheduledPostSchema.pick({
    id: true,
    channelId: true,
    title: true,
    publishAt: true,
    status: true,
    missing: true,
});
/** Kết quả `getMissingForWeek()`: bài đăng trong 48 giờ tới còn thiếu thứ gì. */
export const missingSummarySchema = z.object({
    postId: z.string(),
    channelId: z.string(),
    title: z.string(),
    publishAt: z.iso.datetime(),
    missing: z.array(missingItemSchema),
});
//# sourceMappingURL=schedule.js.map